import * as React from "react";
import { isEqual } from "lodash";
import { XElementConfig } from "../../../utils/interfaces/Elements/XElement.interfaces";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { TUseXElement, useXElement } from "../../../hooks/useXElement";
import { USE_SELECTED_INDEX } from "../../../hooks/useSelectedElement";
import { useSetRecoilState } from "recoil";
import { getValueOnBreakpoint } from "../../../lib/getValueOnBreakpoint";
import { clickHandler } from "../../../lib/clickHandler";
import {
  IXStackProps,
  XStackProps,
} from "../../../utils/interfaces/containers/XStack.interfaces";
import XStackCore from "./core";
import { TDynamicXElement } from "../../../utils/types/core/DynamicXElement";

const XText = React.lazy(() => import("../../elements/XText"));
const XButton = React.lazy(() => import("../../elements/XButton"));
const XRating = React.lazy(() => import("../../elements/XRating"));
const XMap = React.lazy(() => import("../../elements/XMap"));
const XBreadcrumb = React.lazy(() => import("../../elements/XBreadcrumb"));
const XForm = React.lazy(() => import("../../elements/XForm"));
const XIcon = React.lazy(() => import("../../elements/XIcon"));
const XImage = React.lazy(() => import("../../elements/XImage"));

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const XStack: React.FC<XStackProps> = React.memo(
  ({ config = {} as XElementConfig, children }) => {
    // WE TRACK THE BREAK POINTS CHANGES
    const breakpoint = useBreakpoint();

    // WE ARE GETTING THE CURRENT ELEMENT FROM THE ATOM FAMILY
    const { xElement } = useXElement<TUseXElement<IXStackProps>>(config.index);

    // GARBING THE INDEX UPDATER FOR GENERAL USAGE
    const setSelectedElement = useSetRecoilState(USE_SELECTED_INDEX);

    // IF WE DONT HAVE THIS ELEMENT CONFIGS ADDED WE DON'T SHOW IT
    if ((!xElement || Object.keys(xElement).length === 0) && !children) {
      return null;
    }

    // WE GET ALL DEVICES PROPS AS VALUE
    const { value } = xElement;

    // WE NOW USE THE CURRENT BREAKPOINT VALUE ON THIS ELEMENT
    const valueAtBreakPoint = getValueOnBreakpoint<IXStackProps>(
      value as any,
      breakpoint
    );

    const devProps = {
      id: config.index,
      onClick: (e: any) => clickHandler(e, setSelectedElement, config.index),
    };

    return (
      <XStackCore
        index={config.index}
        props={valueAtBreakPoint}
        DynamicXElement={DynamicXElement}
        children={children}
        devProps={devProps}
      />
    );
  },
  compareProps
);

export default XStack;

const DynamicXElement: TDynamicXElement = React.memo(({ index, hierachy }) => {
  const { xElement: element, setXElement } =
    useXElement<TUseXElement<any>>(index);

  const xElement = element ?? {};

  React.useEffect(() => {
    setXElement({
      ...xElement,

      hierachy: hierachy + "-" + xElement.name,
    });
  }, [index]);

  switch (xElement.name) {
    case "XText":
      return (
        <React.Suspense fallback={<p>loading XText...</p>}>
          <XText config={{ index }} />
        </React.Suspense>
      );

    case "XButton":
      return (
        <React.Suspense fallback={<p>loading XButton...</p>}>
          <XButton config={{ index }} />
        </React.Suspense>
      );

    case "XImage":
      return (
        <React.Suspense fallback={<p>loading XImage...</p>}>
          <XImage config={{ index }} />
        </React.Suspense>
      );

    case "XRating":
      return (
        <React.Suspense fallback={<p>loading XRating...</p>}>
          <XRating config={{ index }} />
        </React.Suspense>
      );

    case "XIcon":
      return (
        <React.Suspense fallback={<p>loading XIcon...</p>}>
          <XIcon config={{ index }} />
        </React.Suspense>
      );

    case "XMap":
      return (
        <React.Suspense fallback={<p>loading XMap...</p>}>
          <XMap config={{ index }} />
        </React.Suspense>
      );

    case "XBreadcrumb":
      return (
        <React.Suspense fallback={<p>loading XBreadcrumb...</p>}>
          <XBreadcrumb config={{ index }} />
        </React.Suspense>
      );

    case "XForm":
      return (
        <React.Suspense fallback={<p>loading XForm...</p>}>
          <XForm config={{ index }} />
        </React.Suspense>
      );

    case "XStack":
      return <XStack config={{ index }} />;

    default:
      return null;
  }
}, compareProps);
